import { createApi } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { baseQueryWithAuth } from '../auth';
import { APIVersion } from '../../constants/keys';

const transformErrorResponse = (response: FetchBaseQueryError) =>
  (response as { data?: { errors?: unknown } }).data?.errors;

interface RegisterArgs {
  username: string;
  firstName: string;
  lastName: string;
}

interface LoginArgs {
  username: string;
  password: string;
}
interface LoginResponse {
  refresh: string;
  access: string;
}

interface ResetPasswordArgs {
  username: string;
  password: string;
  token: string;
}

interface ForgotPasswordArgs {
  username: string;
}

// Auth
export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    register: builder.mutation<void, RegisterArgs>({
      query: ({ username, firstName, lastName }) => ({
        url: `/iam-api/${APIVersion}/signup`,
        method: 'POST',
        body: { username, first_name: firstName, last_name: lastName },
      }),
      transformErrorResponse,
    }),
    login: builder.mutation<LoginResponse, LoginArgs>({
      query: ({ username, password }) => ({
        url: `/iam-api/${APIVersion}/login`,
        method: 'POST',
        body: { username, password },
      }),
      transformErrorResponse,
    }),
    loginOptions: builder.query({
      query: () => ({
        url: `/iam-api/${APIVersion}/login`,
        method: 'OPTIONS',
      }),
    }),
    resetPassword: builder.mutation<LoginResponse, ResetPasswordArgs>({
      query: ({ username, password, token }) => ({
        url: `/iam-api/${APIVersion}/password-reset`,
        method: 'POST',
        body: { username, password, password_reset_token: token },
      }),
      transformErrorResponse,
    }),
    forgotPassword: builder.mutation<void, ForgotPasswordArgs>({
      query: ({ username }) => ({
        url: `/iam-api/${APIVersion}/forgot`,
        method: 'POST',
        body: { username },
      }),
      transformErrorResponse,
    }),
  }),
});

export const {
  useLoginMutation,
  useLoginOptionsQuery,
  useRegisterMutation,
  useResetPasswordMutation,
  useForgotPasswordMutation,
} = authApi;
